/**
 * Materialsuche im Frontend über den Artikelkatalog (einmal geladen, auf dem Gerät gespeichert).
 * - alle Wörter müssen vorkommen (Teilwörter), in Nummer, Titel, Beschreibung oder EAN
 * - Gross/Klein, Akzente, «2,5»/«2.5» und «10x10»/«10×10» spielen keine Rolle
 * - Reihenfolge: genaue Nummer, Titel beginnt mit …, Nummer beginnt mit …, Titel enthält …, Rest
 */

export const norm = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/\p{M}/gu, '').replace(/ß/g, 'ss').replace(/,/g, '.').replace(/[×✕]/g, 'x');

export const istCode = (term) => /^\d{8,14}$/.test(String(term ?? '').trim());

/** Vorbereiteter Suchindex (einmal pro Katalog), damit jeder Tastendruck nur noch vergleicht. */
export function suchindex(artikel) {
  return (artikel ?? []).map((a) => {
    const nummer = norm(a.nummer);
    const titel = norm(a.titel);
    return { a, nummer, titel, ean: norm(a.ean), hay: `${nummer} ${titel} ${norm(a.beschreibung)} ${norm(a.ean)}` };
  });
}

/**
 * @param {ReturnType<typeof suchindex>} index
 * @param {string} term
 * @returns {object[]} Artikel
 */
export function sucheArtikel(index, term, limit = 60) {
  const q = norm(term).trim();
  if (q.length < 2) return [];
  if (istCode(q)) return index.filter((x) => x.nummer === q || x.ean === q).map((x) => x.a).slice(0, limit);
  const words = q.split(/\s+/).filter(Boolean);
  const phrase = words.join(' ');
  const hits = [];
  for (const x of index) {
    if (!words.every((w) => x.hay.includes(w))) continue;
    const rang = x.nummer === phrase ? 0 : x.titel.startsWith(phrase) ? 1 : x.nummer.startsWith(phrase) ? 2 : x.titel.includes(phrase) ? 3 : 4;
    hits.push([rang, x]);
  }
  return hits
    .sort((p, r) => p[0] - r[0] || p[1].titel.localeCompare(r[1].titel, 'de'))
    .slice(0, limit)
    .map(([, x]) => x.a);
}

// ─── Gerätekopie (kompakt, unter «bs.» → beim Abmelden gelöscht) ─────────────

const KEY = 'bs.artikelkatalog';
const FELDER = ['id', 'nummer', 'titel', 'beschreibung', 'einheit', 'ean', 'bildUrl'];

export function katalogSpeichern(katalog, geladenAm = Date.now()) {
  try {
    const rows = katalog.artikel.map((a) => FELDER.map((f) => a[f] ?? null));
    window.localStorage.setItem(KEY, JSON.stringify({ v: 1, geladenAm, stand: katalog.stand, vollstaendig: katalog.vollstaendig, rows }));
  } catch {
    // Speicher voll oder gesperrt: dann eben nur im Arbeitsspeicher
    try { window.localStorage.removeItem(KEY); } catch { /* ignorieren */ }
  }
}

/** @returns {{ katalog: { artikel: object[], stand: string, vollstaendig: boolean }, geladenAm: number } | null} */
export function katalogLaden() {
  try {
    const raw = JSON.parse(window.localStorage.getItem(KEY) ?? 'null');
    if (raw?.v !== 1 || !Array.isArray(raw.rows)) return null;
    const artikel = raw.rows.map((r) => Object.fromEntries(FELDER.map((f, i) => [f, r[i]])));
    return { katalog: { artikel, stand: raw.stand, vollstaendig: raw.vollstaendig !== false }, geladenAm: Number(raw.geladenAm) || 0 };
  } catch {
    return null;
  }
}
